import {
  MonthlyDataResponse,
  useChartData,
  useSite,
} from "@inrange/building-manager-api-client";
import { isSiteOwnerOccupied } from "@inrange/theme-components";
import ReactECharts from "echarts-for-react";
import { useEffect, useMemo, useState } from "react";
import { Card, Col, Container, Form, Row, Table } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import { DateRangePicker } from "../../components";
import Loading from "../Loading";

// Colors for different forecast types
const colors = {
  actualDemand: "#8e44ad",
  p50Demand: "#1f3c88",
  p90Demand: "#2e5ac9",

  actualGeneration: "#a51d0eff",
  p50Generation: "#ff6600ff",
  p90Generation: "#ffb27eff",
  curtailmentOverlay: "#e8a07a",

  actualBehindMeter: "#ff9d00ff",
  p50BehindMeter: "#ecc900ff",
  p90BehindMeter: "#ffdd1fff",

  actualExported: "#e91e63",
  p50Exported: "#ff3df5ff",
  p90Exported: "#ffaafcff",

  actualRevenue: "#00ca00ff",
  p50Revenue: "#00a193ff",
  p90Revenue: "#6ac7bfff",

  actualImport: "#2e86c1",
  forecastImport: "#85c1e9",
};

// Helper to determine if a month has only partial actual data
const isPartialMonth = (point: any): boolean => {
  if (!point.actualMetadata) return false;
  const { firstDay, lastDay } = point.actualMetadata;
  const [year, month] = point.date.substring(0, 7).split("-").map(Number);
  const expectedFirstDay = `${year}-${String(month).padStart(2, "0")}-01`;
  const daysInMonth = new Date(year, month, 0).getDate();
  const expectedLastDay = `${year}-${String(month).padStart(2, "0")}-${String(daysInMonth).padStart(2, "0")}`;
  return firstDay !== expectedFirstDay || lastDay !== expectedLastDay;
};

const getMonthLabel = (point: any, html: boolean): string => {
  const label = point.date.substring(0, 7);
  if (!isPartialMonth(point)) return label;
  const { firstDay, lastDay } = point.actualMetadata;
  const [year, month] = label.split("-").map(Number);
  const expectedFirstDay = `${year}-${String(month).padStart(2, "0")}-01`;
  const br = html ? "<br/>" : "\n";
  if (firstDay !== expectedFirstDay) {
    // First partial month - forecast also covers only this range
    return `${firstDay} - ${lastDay}`;
  }
  // Later partial month - forecast covers full month, actuals are partial
  return `${label}${br}Actuals up to ${lastDay}`;
};

// Helper function to create data table with Actual, P50 / Actual %, P90 / Actual %
const createDataTable = (
  chartData: any[],
  p50Field: string,
  actualField: string,
  p90Field: string | null,
  p50Label: string,
  actualLabel: string,
  p90Label: string | null,
  showPercentActual: boolean,
  decimalPlaces: number = 0,
  extraRows?: { label: string; field: string; percentOfField?: string }[]
) => {
  if (chartData.length === 0) return null;

  const months = chartData.map((p) => getMonthLabel(p, false));

  // Helper to format values
  const formatValue = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "-";
    return value.toLocaleString(undefined, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  };

  // Helper to calculate Actual % Forecast
  const calcActualPercentOfForecast = (
    forecast: number | null | undefined,
    actual: number | null | undefined
  ) => {
    if (
      actual === null ||
      actual === undefined ||
      forecast === null ||
      forecast === undefined ||
      forecast === 0
    ) {
      return "-";
    }
    const percent = (actual / forecast - 1) * 100;
    return `${percent >= 0 ? "+" : ""}${percent.toFixed(1)}%`;
  };

  // Helper to format forecast with percentage
  const formatForecastWithPercent = (
    forecast: number | null | undefined,
    actual: number | null | undefined
  ) => {
    const value = formatValue(forecast);
    if (value === "-") return "-";

    // If there's no actual data, just show the forecast value
    if (actual === null || actual === undefined) {
      return value;
    }

    // If showPercentActual is false, just show the value
    if (!showPercentActual) {
      return value;
    }

    const percent = calcActualPercentOfForecast(forecast, actual);
    if (percent === "-") return value; // Show just value if percentage can't be calculated
    return `${value} / ${percent}`;
  };

  // Extract data for each series
  const p50Data = chartData.map((p) => p[p50Field]);
  const actualData = chartData.map((p) => p[actualField]);
  const p90Data = p90Field ? chartData.map((p) => p[p90Field]) : null;

  return (
    <div className="mt-3" style={{ overflowX: "auto" }}>
      <Table bordered size="sm" responsive style={{ fontSize: "0.85rem" }}>
        <thead>
          <tr>
            <th style={{ minWidth: "150px" }}>Metric</th>
            {months.map((month, idx) => (
              <th
                key={idx}
                style={{
                  minWidth: "120px",
                  textAlign: "right",
                  whiteSpace: "pre-line",
                }}
              >
                {month}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>{actualLabel}</strong>
            </td>
            {actualData.map((val, idx) => (
              <td key={idx} style={{ textAlign: "right" }}>
                {formatValue(val)}
              </td>
            ))}
          </tr>
          <tr>
            <td>
              <strong>{p50Label}</strong>
            </td>
            {p50Data.map((val, idx) => (
              <td key={idx} style={{ textAlign: "right" }}>
                {formatForecastWithPercent(val, actualData[idx])}
              </td>
            ))}
          </tr>
          {p90Data && p90Label && (
            <tr>
              <td>
                <strong>{p90Label}</strong>
              </td>
              {p90Data.map((val, idx) => (
                <td key={idx} style={{ textAlign: "right" }}>
                  {formatForecastWithPercent(val, actualData[idx])}
                </td>
              ))}
            </tr>
          )}
          {extraRows?.map((row) => {
            const rowData = chartData.map((p) => p[row.field]);
            const percentOfData = row.percentOfField
              ? chartData.map((p) => p[row.percentOfField!])
              : null;
            return (
              <tr key={row.field}>
                <td>
                  <strong>{row.label}</strong>
                </td>
                {rowData.map((val, idx) => (
                  <td key={idx} style={{ textAlign: "right" }}>
                    {val == null
                      ? "-"
                      : percentOfData
                        ? `${formatValue(val)}${percentOfData[idx] != null && val !== 0 ? ` / ${(percentOfData[idx] / val - 1) * 100 >= 0 ? "+" : ""}${((percentOfData[idx] / val - 1) * 100).toFixed(1)}%` : ""}`
                        : formatValue(val)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

// Helper function to create chart options
const createChartOptions = (
  chartData: any[],
  actualField: string,
  p50Field: string,
  p90Field: string | null,
  actualLabel: string,
  p50Label: string,
  p90Label: string | null,
  yAxisName: string,
  colors: any,
  curtailment?: {
    field: string;
    label: string;
    color: string;
  }
) => {
  if (chartData.length === 0) return {};

  const xAxisData = chartData.map((point) => point.date.substring(0, 7));
  const tooltipLabels = chartData.map((point) => getMonthLabel(point, true));

  const series = [
    {
      name: p50Label,
      type: "line",
      data: chartData.map((p) => p[p50Field] ?? null),
      itemStyle: { color: colors.p50 },
      lineStyle: { width: 2 },
      symbolSize: 4,
    },
  ];

  const legendData = [actualLabel, p50Label];

  if (p90Field && p90Label) {
    series.push({
      name: p90Label,
      type: "line",
      data: chartData.map((p) => p[p90Field] ?? null),
      itemStyle: { color: colors.p90 },
      lineStyle: { width: 2 },
      symbolSize: 4,
    });
    legendData.push(p90Label);
  }

  // Add actual data last so it renders on top
  series.push({
    name: actualLabel,
    type: "line",
    data: chartData.map((p) => p[actualField] ?? null),
    itemStyle: { color: colors.actual },
    lineStyle: { width: 3 },
    symbolSize: 5,
    z: 10, // Ensure it's rendered on top
  } as any);

  // Add "actual + estimated curtailed" overlay if requested. Only months with
  // curtailment data contribute — months without data show the same value as
  // actual (the two lines overlap).
  if (curtailment) {
    const hasAnyCurtailment = chartData.some(
      (p) => (p[curtailment.field] ?? 0) > 0
    );
    if (hasAnyCurtailment) {
      series.push({
        name: curtailment.label,
        type: "line",
        data: chartData.map((p) => {
          const actualVal = p[actualField];
          const lost = p[curtailment.field];
          if (actualVal == null) return null;
          return actualVal + (lost ?? 0);
        }),
        itemStyle: { color: curtailment.color },
        lineStyle: { width: 2, type: "dashed" },
        symbolSize: 4,
        z: 9,
      } as any);
      legendData.push(curtailment.label);
    }
  }

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "line",
      },
      formatter: (params: any) => {
        const idx = params[0]?.dataIndex ?? 0;
        const header = tooltipLabels[idx] || params[0]?.axisValue;
        const lines = params.map(
          (p: any) =>
            `${p.marker} ${p.seriesName}: ${p.value != null ? p.value.toLocaleString() : "-"}`
        );
        return `<strong>${header}</strong><br/>${lines.join("<br/>")}`;
      },
    },
    legend: {
      data: legendData,
      top: 0,
    },
    grid: {
      left: "60px",
      right: "20px",
      bottom: "60px",
      top: "80px",
      containLabel: false,
    },
    xAxis: {
      type: "category",
      data: xAxisData,
      axisLabel: {
        rotate: 45,
      },
    },
    yAxis: {
      type: "value",
      name: yAxisName,
    },
    series,
  };
};

const SitePerformance = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize from URL query string so a deep-link / reload restores the
  // user's selections. Empty strings fall back to the data-driven defaults
  // applied by the effect below once chart data loads.
  const initialStartMonth = searchParams.get("startMonth") || "";
  const initialEndMonth = searchParams.get("endMonth") || "";
  const initialViewMode = (() => {
    const v = searchParams.get("viewMode");
    return v === "cumulative" ? "cumulative" : "monthly";
  })();

  const [monthPeriod, setMonthPeriod] = useState<string>("");
  const [startMonth, setStartMonth] = useState<string>(initialStartMonth);
  const [endMonth, setEndMonth] = useState<string>(initialEndMonth);
  const [viewMode, setViewMode] = useState<"monthly" | "cumulative">(initialViewMode);
  const [showPercentActual, setShowPercentActual] = useState<boolean>(true);

  const { fetchSite } = useSite({
    siteId,
    app: "admin",
  });

  // Fetch monthly forecast/actual data
  const chartDataQuery = useChartData({
    dataType: "monthly_forecast_actual",
    options: {},
    siteId: siteId!,
    enabled: true,
  });

  const site = fetchSite.data?.site;
  const isLoading = chartDataQuery.isLoading;
  const error = chartDataQuery.error;

  // Compute month presets based on API data
  const monthPresets = useMemo(() => {
    if (!chartDataQuery.data) return [];

    const data = (chartDataQuery.data as MonthlyDataResponse).data;
    if (data.length === 0) return [];

    const allMonths = data.map((point) => point.date.substring(0, 7)).sort();
    const firstForecastMonth = allMonths[0];
    const lastForecastMonth = allMonths[allMonths.length - 1];

    // Find the first month with actual data
    const monthsWithActuals = data
      .filter(
        (point) =>
          point.aDemand !== undefined ||
          point.aGeneration !== undefined ||
          point.aNetworkImport !== undefined
      )
      .map((point) => point.date.substring(0, 7))
      .sort();

    const presets: Array<{
      key: string;
      label: string;
      start: string;
      end: string;
    }> = [];

    // Add "Latest 12 months actuals" and "All actuals" options
    if (monthsWithActuals.length > 0) {
      const firstActualMonth = monthsWithActuals[0];
      const lastActualMonth = monthsWithActuals[monthsWithActuals.length - 1];

      // Calculate the number of months with actuals
      const [firstYear, firstMonth] = firstActualMonth.split("-").map(Number);
      const [lastYear, lastMonth] = lastActualMonth.split("-").map(Number);
      const monthsDiff =
        (lastYear - firstYear) * 12 + (lastMonth - firstMonth) + 1;

      let latest12Start: string;
      let latest12End: string;

      if (monthsDiff < 12) {
        // If fewer than 12 months of actuals, start with first actual and extend to 12 months
        latest12Start = firstActualMonth;
        const extendedYear = firstYear + Math.floor((firstMonth - 1 + 11) / 12);
        const extendedMonth = ((firstMonth - 1 + 11) % 12) + 1;
        latest12End = `${extendedYear}-${String(extendedMonth).padStart(2, "0")}`;
      } else {
        // If 12 or more months of actuals, show the latest 12 months (ending with last actual)
        latest12End = lastActualMonth;
        const lastMonthTotal = lastYear * 12 + lastMonth;
        const startMonthTotal = lastMonthTotal - 11;
        const startYear = Math.floor((startMonthTotal - 1) / 12);
        const startMonth = ((startMonthTotal - 1) % 12) + 1;
        latest12Start = `${startYear}-${String(startMonth).padStart(2, "0")}`;
      }

      presets.push({
        key: "latest-12-months-actuals",
        label: "Latest 12 months actuals",
        start: latest12Start,
        end: latest12End,
      });

      // Calculate minimum 12 month period from first actual for "All actuals"
      let actualsEnd = lastActualMonth;
      if (monthsDiff < 12) {
        const extendedYear = firstYear + Math.floor((firstMonth - 1 + 11) / 12);
        const extendedMonth = ((firstMonth - 1 + 11) % 12) + 1;
        actualsEnd = `${extendedYear}-${String(extendedMonth).padStart(2, "0")}`;
      }

      presets.push({
        key: "all-actuals",
        label: "All actuals",
        start: firstActualMonth,
        end: actualsEnd,
      });

      // Create 12-month periods starting from the first actual month
      const [startYear, startMonth] = firstActualMonth.split("-").map(Number);
      let currentYear = startYear;
      let currentMonth = startMonth;
      let continueLoop = true;

      while (continueLoop) {
        const periodStart = `${currentYear}-${String(currentMonth).padStart(2, "0")}`;

        // Calculate end month (11 months after start)
        let endYear = currentYear;
        let endMonth = currentMonth + 11;

        if (endMonth > 12) {
          endYear = currentYear + Math.floor((endMonth - 1) / 12);
          endMonth = ((endMonth - 1) % 12) + 1;
        }

        const periodEnd = `${endYear}-${String(endMonth).padStart(2, "0")}`;

        // Stop if this period would start after the last actual month
        if (periodStart > lastActualMonth) {
          continueLoop = false;
          break;
        }

        presets.push({
          key: `${periodStart}-to-${periodEnd}`,
          label: `${periodStart} to ${periodEnd}`,
          start: periodStart,
          end: periodEnd,
        });

        // Move to next period (12 months forward)
        currentMonth += 12;
        if (currentMonth > 12) {
          currentYear += Math.floor((currentMonth - 1) / 12);
          currentMonth = ((currentMonth - 1) % 12) + 1;
        }
      }

      // Add "All forecast" option
      presets.push({
        key: "all-forecast",
        label: "All forecast",
        start: firstForecastMonth,
        end: lastForecastMonth,
      });
    } else {
      // If no actuals, add "First 12 months forecast" option
      const [firstYear, firstMonth] = firstForecastMonth.split("-").map(Number);

      // Calculate end month (11 months after start = 12 months total)
      let endYear = firstYear;
      let endMonth = firstMonth + 11;

      if (endMonth > 12) {
        endYear = firstYear + Math.floor((endMonth - 1) / 12);
        endMonth = ((endMonth - 1) % 12) + 1;
      }

      const first12End = `${endYear}-${String(endMonth).padStart(2, "0")}`;

      presets.push({
        key: "first-12-months-forecast",
        label: "First 12 months forecast",
        start: firstForecastMonth,
        end: first12End,
      });
    }

    return presets;
  }, [chartDataQuery.data]);

  // Compute default period based on actuals data
  const defaultPeriod = useMemo(() => {
    if (!chartDataQuery.data) return null;

    const data = (chartDataQuery.data as MonthlyDataResponse).data;
    if (data.length === 0) return null;

    // Find the first and last month with actual data
    const monthsWithActuals = data
      .filter(
        (point) =>
          point.aDemand !== undefined ||
          point.aGeneration !== undefined ||
          point.aNetworkImport !== undefined
      )
      .map((point) => point.date.substring(0, 7))
      .sort();

    if (monthsWithActuals.length === 0) {
      // If no actuals, use first 12 months of forecast data
      const allMonths = data.map((point) => point.date.substring(0, 7)).sort();
      const firstMonth = allMonths[0];

      // Calculate end month (11 months after start = 12 months total)
      const [firstYear, firstMonthNum] = firstMonth.split("-").map(Number);
      let endYear = firstYear;
      let endMonth = firstMonthNum + 11;

      if (endMonth > 12) {
        endYear = firstYear + Math.floor((endMonth - 1) / 12);
        endMonth = ((endMonth - 1) % 12) + 1;
      }

      const lastMonth = `${endYear}-${String(endMonth).padStart(2, "0")}`;
      return { start: firstMonth, end: lastMonth };
    }

    const firstActualMonth = monthsWithActuals[0];
    const lastActualMonth = monthsWithActuals[monthsWithActuals.length - 1];

    // Calculate the number of months with actuals
    const [firstYear, firstMonth] = firstActualMonth.split("-").map(Number);
    const [lastYear, lastMonth] = lastActualMonth.split("-").map(Number);
    const monthsDiff =
      (lastYear - firstYear) * 12 + (lastMonth - firstMonth) + 1;

    // If fewer than 12 months of actuals, start with first actual and extend to 12 months
    if (monthsDiff < 12) {
      const extendedYear = firstYear + Math.floor((firstMonth - 1 + 11) / 12);
      const extendedMonth = ((firstMonth - 1 + 11) % 12) + 1;
      const endMonth = `${extendedYear}-${String(extendedMonth).padStart(2, "0")}`;
      return { start: firstActualMonth, end: endMonth };
    }

    // If 12 or more months of actuals, show the latest 12 months (ending with last actual)
    // Calculate start month (11 months before last actual = 12 months total)
    const lastMonthTotal = lastYear * 12 + lastMonth;
    const startMonthTotal = lastMonthTotal - 11;
    const startYear = Math.floor((startMonthTotal - 1) / 12);
    const startMonth = ((startMonthTotal - 1) % 12) + 1;
    const start = `${startYear}-${String(startMonth).padStart(2, "0")}`;

    return { start, end: lastActualMonth };
  }, [chartDataQuery.data]);

  // Set initial period when data is loaded.
  //
  // Two paths:
  //   - URL provided startMonth + endMonth: resolve monthPeriod by matching
  //     against the freshly-computed presets (or fall back to "custom").
  //     Don't touch the months themselves — they're already set from the URL.
  //   - No URL values: apply the data-driven default ("latest 12 months
  //     actuals" or "first 12 months forecast" depending on data availability).
  useEffect(() => {
    if (monthPeriod) return; // already initialised

    if (startMonth && endMonth) {
      const matchingPreset = monthPresets.find(
        (preset) => preset.start === startMonth && preset.end === endMonth
      );
      setMonthPeriod(
        matchingPreset
          ? `${matchingPreset.key}|${matchingPreset.start}|${matchingPreset.end}`
          : "custom"
      );
      return;
    }

    if (defaultPeriod) {
      // Check if there are any actuals
      const data = chartDataQuery.data
        ? (chartDataQuery.data as MonthlyDataResponse).data
        : [];
      const hasActuals = data.some(
        (point) =>
          point.aDemand !== undefined ||
          point.aGeneration !== undefined ||
          point.aNetworkImport !== undefined
      );

      const periodKey = hasActuals
        ? "latest-12-months-actuals"
        : "first-12-months-forecast";
      setMonthPeriod(
        `${periodKey}|${defaultPeriod.start}|${defaultPeriod.end}`
      );
      setStartMonth(defaultPeriod.start);
      setEndMonth(defaultPeriod.end);
    }
  }, [defaultPeriod, monthPeriod, chartDataQuery.data, startMonth, endMonth, monthPresets]);

  // Persist startMonth, endMonth, and viewMode to URL query params so a deep
  // link / page reload restores the same selections. Only writes once
  // monthPeriod is set, otherwise we'd overwrite URL-provided startMonth /
  // endMonth before the init effect above has had a chance to use them.
  useEffect(() => {
    if (!monthPeriod) return;
    setSearchParams(
      (params) => {
        if (startMonth) {
          params.set("startMonth", startMonth);
        } else {
          params.delete("startMonth");
        }
        if (endMonth) {
          params.set("endMonth", endMonth);
        } else {
          params.delete("endMonth");
        }
        if (viewMode !== "monthly") {
          params.set("viewMode", viewMode);
        } else {
          params.delete("viewMode");
        }
        return params;
      },
      { replace: true }
    );
  }, [startMonth, endMonth, viewMode, monthPeriod, setSearchParams]);

  // Filter and format data for the chart
  const chartData = useMemo(() => {
    if (!chartDataQuery.data) return [];

    const data = (chartDataQuery.data as MonthlyDataResponse).data;

    // Filter data by selected month range
    const filteredData = data.filter((point) => {
      const pointMonth = point.date.substring(0, 7); // Extract YYYY-MM
      return pointMonth >= startMonth && pointMonth <= endMonth;
    });

    // If cumulative mode, calculate cumulative sums
    if (viewMode === "cumulative") {
      // Get all unique field names from ALL data points (not just the first one)
      const allFieldsSet = new Set<string>();
      filteredData.forEach((point) => {
        Object.keys(point).forEach((key) => {
          if (typeof point[key] === "number") {
            allFieldsSet.add(key);
          }
        });
      });

      // Separate fields by prefix: 'f' for forecast, 'a' for actual
      const forecastFields = Array.from(allFieldsSet).filter((field) =>
        field.startsWith("f")
      );
      const actualFields = Array.from(allFieldsSet).filter((field) =>
        field.startsWith("a")
      );

      // Find the first index where we have BOTH actuals and forecast data
      // If there are no actuals at all, start from the first forecast data
      let startAccumulationIndex = -1;

      // Check if we have any actual data at all
      const hasAnyActuals = filteredData.some((point) =>
        actualFields.some(
          (field) => point[field] !== undefined && point[field] !== null
        )
      );

      if (!hasAnyActuals) {
        // No actuals data - start from the first month with forecast data
        for (let i = 0; i < filteredData.length; i++) {
          const point = filteredData[i];
          const hasForecast = forecastFields.some(
            (field) => point[field] !== undefined && point[field] !== null
          );
          if (hasForecast) {
            startAccumulationIndex = i;
            break;
          }
        }
      } else {
        // Has actuals - find where we have BOTH actuals and forecast data
        for (let i = 0; i < filteredData.length; i++) {
          const point = filteredData[i];
          const hasActual = actualFields.some(
            (field) => point[field] !== undefined && point[field] !== null
          );
          const hasForecast = forecastFields.some(
            (field) => point[field] !== undefined && point[field] !== null
          );
          if (hasActual && hasForecast) {
            startAccumulationIndex = i;
            break;
          }
        }
      }

      // For each actual field, find the last index where we have data
      const lastActualIndex: Record<string, number> = {};
      actualFields.forEach((field) => {
        let lastIdx = -1;
        for (let i = filteredData.length - 1; i >= 0; i--) {
          const value = filteredData[i][field];
          if (value !== undefined && value !== null) {
            lastIdx = i;
            break;
          }
        }
        lastActualIndex[field] = lastIdx;
      });

      const cumulativeData = filteredData.map((point, index) => {
        // Before accumulation starts, return undefined for all cumulative values
        if (startAccumulationIndex === -1 || index < startAccumulationIndex) {
          const empty = { ...point };
          [...forecastFields, ...actualFields].forEach((field) => {
            (empty as any)[field] = undefined;
          });
          return empty;
        }

        // Sum from startAccumulationIndex to current index
        const cumulative = { ...point };

        // Accumulate forecast fields
        forecastFields.forEach((field) => {
          let sum = 0;
          for (let i = startAccumulationIndex; i <= index; i++) {
            const value = filteredData[i][field];
            if (value !== undefined && value !== null) {
              sum += value;
            }
          }
          (cumulative as any)[field] = Math.round(sum * 100) / 100;
        });

        // For actual fields, only accumulate up to the last available data point
        actualFields.forEach((field) => {
          const lastIdx = lastActualIndex[field];
          if (lastIdx === -1 || index > lastIdx) {
            // No actual data for this field, or we're past the last actual data point
            (cumulative as any)[field] = undefined;
          } else if (index < startAccumulationIndex) {
            // Before accumulation starts
            (cumulative as any)[field] = undefined;
          } else {
            let sum = 0;
            for (let i = startAccumulationIndex; i <= index; i++) {
              const value = filteredData[i][field];
              if (value !== undefined && value !== null) {
                sum += value;
              }
            }
            (cumulative as any)[field] = Math.round(sum * 100) / 100;
          }
        });

        return cumulative;
      });

      return cumulativeData;
    }

    return filteredData;
  }, [chartDataQuery.data, startMonth, endMonth, viewMode]);

  const handleMonthPeriodChange = (period: string) => {
    setMonthPeriod(period);

    // Parse months from the period value if it contains dates
    if (period.includes("|")) {
      const [, start, end] = period.split("|");
      setStartMonth(start);
      setEndMonth(end);
    }
    // "custom" case - don't change months
  };

  const handleMonthChange = (newStartMonth: string, newEndMonth: string) => {
    setStartMonth(newStartMonth);
    setEndMonth(newEndMonth);

    // Find matching preset
    const matchingPreset = monthPresets.find(
      (preset) => preset.start === newStartMonth && preset.end === newEndMonth
    );

    setMonthPeriod(
      matchingPreset
        ? `${matchingPreset.key}|${matchingPreset.start}|${matchingPreset.end}`
        : "custom"
    );
  };

  // Generate chart options for all metrics
  const hasCurtailmentData = useMemo(
    () => chartData.some((p) => (p.aCurtailedKwh ?? 0) > 0),
    [chartData]
  );

  const generationChartOptions = useMemo(() => {
    return createChartOptions(
      chartData,
      "aGeneration",
      "fP50Generation",
      "fP90Generation",
      "Actual Generation",
      "Forecast Generation (P50)",
      "Forecast Generation (P90)",
      "Energy (kWh)",
      {
        actual: colors.actualGeneration,
        p50: colors.p50Generation,
        p90: colors.p90Generation,
      },
      hasCurtailmentData
        ? {
            field: "aCurtailedKwh",
            label: "Actual + Est curtailed",
            color: colors.curtailmentOverlay,
          }
        : undefined
    );
  }, [chartData, hasCurtailmentData]);

  const generationTable = useMemo(() => {
    return createDataTable(
      chartData,
      "fP50Generation",
      "aGeneration",
      "fP90Generation",
      showPercentActual
        ? "Forecast Generation (P50) / Actual %"
        : "Forecast Generation (P50)",
      "Actual Generation",
      showPercentActual
        ? "Forecast Generation (P90) / Actual %"
        : "Forecast Generation (P90)",
      showPercentActual,
      3,
      hasCurtailmentData
        ? [{ label: "Est curtailed", field: "aCurtailedKwh" }]
        : undefined
    );
  }, [chartData, showPercentActual, hasCurtailmentData]);

  const behindMeterChartOptions = useMemo(() => {
    return createChartOptions(
      chartData,
      "aBehindMeter",
      "fP50BehindMeter",
      "fP90BehindMeter",
      "Actual Behind Meter",
      "Forecast Behind Meter (P50)",
      "Forecast Behind Meter (P90)",
      "Energy (kWh)",
      {
        actual: colors.actualBehindMeter,
        p50: colors.p50BehindMeter,
        p90: colors.p90BehindMeter,
      }
    );
  }, [chartData]);

  const behindMeterTable = useMemo(() => {
    return createDataTable(
      chartData,
      "fP50BehindMeter",
      "aBehindMeter",
      "fP90BehindMeter",
      showPercentActual
        ? "Forecast Behind Meter (P50) / Actual %"
        : "Forecast Behind Meter (P50)",
      "Actual Behind Meter",
      showPercentActual
        ? "Forecast Behind Meter (P90) / Actual %"
        : "Forecast Behind Meter (P90)",
      showPercentActual,
      3
    );
  }, [chartData, showPercentActual]);

  // Combine grid export + network export for the export chart/table
  const exportChartData = useMemo(() => {
    return chartData.map((p) => {
      const add = (a?: number, b?: number) =>
        a != null || b != null ? (a ?? 0) + (b ?? 0) : undefined;
      return {
        ...p,
        aExported: add(p.aExported, p.aNetworkExport),
        fP50Exported: add(p.fP50Exported, p.fP50NetworkExport),
        fP90Exported: add(p.fP90Exported, p.fP90NetworkExport),
      };
    });
  }, [chartData]);

  const exportChartOptions = useMemo(() => {
    const options = createChartOptions(
      exportChartData,
      "aExported",
      "fP50Exported",
      "fP90Exported",
      "Actual Exported",
      "Forecast Exported (P50)",
      "Forecast Exported (P90)",
      "Energy (kWh)",
      {
        actual: colors.actualExported,
        p50: colors.p50Exported,
        p90: colors.p90Exported,
      }
    );
    // Add supplier metered export series if data is available
    const hasExternalMeasure = exportChartData.some(
      (p) => p.aExportedExternalMeasure != null
    );
    if (hasExternalMeasure && options.series) {
      const label = "Export metered (supplier)";
      options.series.push({
        name: label,
        type: "line",
        data: exportChartData.map(
          (p) => p.aExportedExternalMeasure ?? null
        ),
        itemStyle: { color: "#ff6600" },
        lineStyle: { width: 2 },
        symbolSize: 4,
      } as any);
      options.legend?.data?.push(label);
    }
    return options;
  }, [exportChartData]);

  const exportTable = useMemo(() => {
    return createDataTable(
      exportChartData,
      "fP50Exported",
      "aExported",
      "fP90Exported",
      showPercentActual
        ? "Forecast Exported (P50) / Actual %"
        : "Forecast Exported (P50)",
      "Actual Exported",
      showPercentActual
        ? "Forecast Exported (P90) / Actual %"
        : "Forecast Exported (P90)",
      showPercentActual,
      3,
      exportChartData.some((p) => p.aExportedExternalMeasure != null)
        ? [
            {
              label: showPercentActual
                ? "Export metered (supplier) / Actual %"
                : "Export metered (supplier)",
              field: "aExportedExternalMeasure",
              percentOfField: showPercentActual ? "aExported" : undefined,
            },
          ]
        : undefined
    );
  }, [exportChartData, showPercentActual]);

  const demandChartOptions = useMemo(() => {
    return createChartOptions(
      chartData,
      "aDemand",
      "fP50Demand",
      null,
      "Actual Demand",
      "Forecast Demand",
      null,
      "Energy (kWh)",
      {
        actual: colors.actualDemand,
        p50: colors.p50Demand,
        p90: colors.p90Demand,
      }
    );
  }, [chartData]);

  const demandTable = useMemo(() => {
    return createDataTable(
      chartData,
      "fP50Demand",
      "aDemand",
      null,
      showPercentActual ? "Forecast Demand / Actual %" : "Forecast Demand",
      "Actual Demand",
      null,
      showPercentActual,
      3
    );
  }, [chartData, showPercentActual]);

  const revenueChartOptionsLLLease = useMemo(() => {
    return createChartOptions(
      chartData,
      "aLLLeaseRevenue",
      "fP50LLLeaseRevenue",
      "fP90LLLeaseRevenue",
      "Actual Landlord Lease Revenue",
      "Landlord Lease Revenue (P50)",
      "Landlord Lease Revenue (P90)",
      "Revenue",
      {
        actual: colors.actualRevenue,
        p50: colors.p50Revenue,
        p90: colors.p90Revenue,
      }
    );
  }, [chartData]);

  const revenueTableLLLease = useMemo(() => {
    return createDataTable(
      chartData,
      "fP50LLLeaseRevenue",
      "aLLLeaseRevenue",
      "fP90LLLeaseRevenue",
      showPercentActual
        ? "Landlord Lease Revenue (P50) / Actual %"
        : "Landlord Lease Revenue (P50)",
      "Actual Landlord Lease Revenue",
      showPercentActual
        ? "Landlord Lease Revenue (P90) / Actual %"
        : "Landlord Lease Revenue (P90)",
      showPercentActual,
      2
    );
  }, [chartData, showPercentActual]);

  const revenueChartOptionsLLLicense = useMemo(() => {
    return createChartOptions(
      chartData,
      "aLLLicenseRevenue",
      "fP50LLLicenseRevenue",
      "fP90LLLicenseRevenue",
      "Actual Landlord License Revenue",
      "Landlord License Revenue (P50)",
      "Landlord License Revenue (P90)",
      "Revenue",
      {
        actual: colors.actualRevenue,
        p50: colors.p50Revenue,
        p90: colors.p90Revenue,
      }
    );
  }, [chartData]);

  const revenueTableLLLicense = useMemo(() => {
    return createDataTable(
      chartData,
      "fP50LLLicenseRevenue",
      "aLLLicenseRevenue",
      "fP90LLLicenseRevenue",
      showPercentActual
        ? "Landlord License Revenue (P50) / Actual %"
        : "Landlord License Revenue (P50)",
      "Actual Landlord License Revenue",
      showPercentActual
        ? "Landlord License Revenue (P90) / Actual %"
        : "Landlord License Revenue (P90)",
      showPercentActual,
      2
    );
  }, [chartData, showPercentActual]);

  const revenueChartOptionsOOLease = useMemo(() => {
    return createChartOptions(
      chartData,
      "aOOLeaseRevenue",
      "fP50OOLeaseRevenue",
      "fP90OOLeaseRevenue",
      "Actual Owner Occupier Lease Revenue",
      "P50 Owner Occupier Lease Revenue",
      "P90 Owner Occupier Lease Revenue",
      "Revenue",
      {
        actual: colors.actualRevenue,
        p50: colors.p50Revenue,
        p90: colors.p90Revenue,
      }
    );
  }, [chartData]);

  const revenueTableOOLease = useMemo(() => {
    return createDataTable(
      chartData,
      "fP50OOLeaseRevenue",
      "aOOLeaseRevenue",
      "fP90OOLeaseRevenue",
      showPercentActual
        ? "Owner Occupier Lease Revenue (P50) / Actual %"
        : "Owner Occupier Lease Revenue (P50)",
      "Actual Owner Occupier Lease Revenue",
      showPercentActual
        ? "Owner Occupier Lease Revenue (P90) / Actual %"
        : "Owner Occupier Lease Revenue (P90)",
      showPercentActual,
      2
    );
  }, [chartData, showPercentActual]);

  const revenueChartOptionsOOLicense = useMemo(() => {
    return createChartOptions(
      chartData,
      "aOOLicenseRevenue",
      "fP50OOLicenseRevenue",
      "fP90OOLicenseRevenue",
      "Actual Owner Occupier License Revenue",
      "Owner Occupier License Revenue (P50)",
      "Owner Occupier License Revenue (P90)",
      "Revenue",
      {
        actual: colors.actualRevenue,
        p50: colors.p50Revenue,
        p90: colors.p90Revenue,
      }
    );
  }, [chartData]);

  const revenueTableOOLicense = useMemo(() => {
    return createDataTable(
      chartData,
      "fP50OOLicenseRevenue",
      "aOOLicenseRevenue",
      "fP90OOLicenseRevenue",
      showPercentActual
        ? "Owner Occupier License Revenue (P50) / Actual %"
        : "Owner Occupier License Revenue (P50)",
      "Actual Owner Occupier License Revenue",
      showPercentActual
        ? "Owner Occupier License Revenue (P90) / Actual %"
        : "Owner Occupier License Revenue (P90)",
      showPercentActual,
      2
    );
  }, [chartData, showPercentActual]);

  // Import chart for import-only (procure-only) sites
  const importChartOptions = useMemo(() => {
    return createChartOptions(
      chartData,
      "aNetworkImport",
      "fP50NetworkImport",
      null,
      "Actual Import",
      "Forecast Import",
      null,
      "Energy (kWh)",
      {
        actual: colors.actualImport,
        p50: colors.forecastImport,
      }
    );
  }, [chartData]);

  const importTable = useMemo(() => {
    return createDataTable(
      chartData,
      "fP50NetworkImport",
      "aNetworkImport",
      null,
      showPercentActual
        ? "Forecast Import / Actual %"
        : "Forecast Import",
      "Actual Import",
      null,
      showPercentActual,
      3
    );
  }, [chartData, showPercentActual]);

  if (!fetchSite.isLoading && fetchSite.isError) {
    return <div>Error loading the site.</div>;
  }

  if (fetchSite.isLoading || !site) {
    return <Loading label="Loading site data..." />;
  }

  const isImportOnly = site.totalInstalledCapacity === 0;

  // Determine which revenue chart to show
  const isOwnerOccupied = isSiteOwnerOccupied(site.siteOwnerships);
  const isLease = site.activeInvestmentModel === "lease";

  // Select the appropriate revenue chart options, table, and title
  let revenueChartOptions;
  let revenueTable;
  let revenueTitle;

  if (isOwnerOccupied) {
    if (isLease) {
      revenueChartOptions = revenueChartOptionsOOLease;
      revenueTable = revenueTableOOLease;
      revenueTitle = "Owner Occupier Lease Customer Revenue";
    } else {
      revenueChartOptions = revenueChartOptionsOOLicense;
      revenueTable = revenueTableOOLicense;
      revenueTitle = "Owner Occupier License Customer Revenue";
    }
  } else {
    if (isLease) {
      revenueChartOptions = revenueChartOptionsLLLease;
      revenueTable = revenueTableLLLease;
      revenueTitle = "Landlord Lease Customer Revenue";
    } else {
      revenueChartOptions = revenueChartOptionsLLLicense;
      revenueTable = revenueTableLLLicense;
      revenueTitle = "Landlord License Customer Revenue";
    }
  }

  return (
    <Container fluid style={{ padding: "12px 12px 0px 12px" }}>
        <Card body className="mt-2">
          {/* View Mode */}
          <Row className="mb-3 mt-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>View Mode</Form.Label>
                <Form.Select
                  value={viewMode}
                  onChange={(e) =>
                    setViewMode(e.target.value as "monthly" | "cumulative")
                  }
                >
                  <option value="monthly">Monthly</option>
                  <option value="cumulative">Cumulative</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          {/* Month Period Selection */}
          <Row className="mb-3 mt-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Time Period</Form.Label>
                <Form.Select
                  value={monthPeriod}
                  onChange={(e) => handleMonthPeriodChange(e.target.value)}
                >
                  {monthPeriod === "custom" && (
                    <option value="custom">Custom</option>
                  )}
                  {monthPresets.map((preset) => (
                    <option
                      key={preset.key}
                      value={`${preset.key}|${preset.start}|${preset.end}`}
                    >
                      {preset.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Month Range */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Month Range</Form.Label>
                <DateRangePicker
                  value={{
                    start: startMonth ? new Date(`${startMonth}-01`) : null,
                    end: endMonth ? new Date(`${endMonth}-01`) : null,
                  }}
                  onChange={({ start, end }) => {
                    if (start && end) {
                      const startYear = start.getFullYear();
                      const startMonthNum = (start.getMonth() + 1)
                        .toString()
                        .padStart(2, "0");
                      const endYear = end.getFullYear();
                      const endMonthNum = (end.getMonth() + 1)
                        .toString()
                        .padStart(2, "0");
                      handleMonthChange(
                        `${startYear}-${startMonthNum}`,
                        `${endYear}-${endMonthNum}`
                      );
                    }
                  }}
                  mode="month"
                  startPlaceholder="Start month"
                  endPlaceholder="End month"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Show Actual % Forecast Switch */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Check
                type="switch"
                id="show-percent-actual-switch"
                label="Show Actual % Forecast"
                checked={showPercentActual}
                onChange={(e) => setShowPercentActual(e.target.checked)}
              />
            </Col>
          </Row>

          {/* Loading State */}
          {isLoading && (
            <div className="mt-3">
              <Loading label="Loading performance data..." />
            </div>
          )}

          {/* Error State */}
          {!!error && (
            <div className="alert alert-danger mt-3">
              Error loading data: {String(error)}
            </div>
          )}

          {/* Charts */}
          {chartData.length > 0 && isImportOnly && (
            <div className="mt-4">
              <h5>Import - {site.name}</h5>
              <ReactECharts
                option={importChartOptions}
                style={{ height: "400px", width: "100%" }}
                notMerge={true}
                lazyUpdate={true}
              />
              {importTable}
            </div>
          )}

          {chartData.length > 0 && !isImportOnly && (
            <>
              <div className="mt-4">
                <h5>Generation - {site.name}</h5>
                <ReactECharts
                  option={generationChartOptions}
                  style={{ height: "400px", width: "100%" }}
                  notMerge={true}
                  lazyUpdate={true}
                />
                {generationTable}
              </div>

              <div className="mt-4">
                <h5>Behind Meter - {site.name}</h5>
                <ReactECharts
                  option={behindMeterChartOptions}
                  style={{ height: "400px", width: "100%" }}
                  notMerge={true}
                  lazyUpdate={true}
                />
                {behindMeterTable}
              </div>

              <div className="mt-4">
                <h5>Export - {site.name}</h5>
                <ReactECharts
                  option={exportChartOptions}
                  style={{ height: "400px", width: "100%" }}
                  notMerge={true}
                  lazyUpdate={true}
                />
                {exportTable}
              </div>

              <div className="mt-4">
                <h5>Demand - {site.name}</h5>
                <ReactECharts
                  option={demandChartOptions}
                  style={{ height: "400px", width: "100%" }}
                  notMerge={true}
                  lazyUpdate={true}
                />
                {demandTable}
              </div>

              <div className="mt-4">
                <h5>
                  {revenueTitle} - {site.name}
                </h5>
                <ReactECharts
                  option={revenueChartOptions}
                  style={{ height: "400px", width: "100%" }}
                  notMerge={true}
                  lazyUpdate={true}
                />
                {revenueTable}
              </div>
            </>
          )}

          {/* No Data Message */}
          {chartDataQuery.data && chartData.length === 0 && (
            <div className="alert alert-info mt-3">
              No data available for the selected period.
            </div>
          )}
        </Card>
  </Container>
  );
};

export default SitePerformance;
